/* ==========================================================================
   Noveliya — Image lightbox
   Opens on .nv-gallery__item click inside <nv-gallery>, or on any
   [data-lb-item] inside a [data-nv-lightbox] container (e.g. NV Socials).
   Prev/next navigation via buttons or arrow keys. Escape to close.
   Focus-trapped, scroll-locked while open.
   ========================================================================== */

(function () {
  'use strict';

  /* Several sections may include this script on one page — run once */
  if (window.nvLightboxLoaded) return;
  window.nvLightboxLoaded = true;

  const GALLERY = 'nv-gallery, [data-nv-lightbox]';
  const ITEM    = '.nv-gallery__item, [data-lb-item]';

  let lb, imgEl, prevBtn, nextBtn, closeBtn, counterEl;
  let images    = [];
  let current   = 0;
  let lastFocus = null;
  let built     = false;

  /* ── Build DOM (once, on first open) ─────────────────────────────────────── */

  function build() {
    if (built) return;
    built = true;

    lb = document.createElement('div');
    lb.className = 'nv-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Afbeeldingenviewer');
    lb.setAttribute('aria-hidden', 'true');

    /* Close uses the same filled path as icon-close.svg for cart-drawer parity */
    lb.innerHTML = [
      '<button class="nv-lb__close" data-lb-close aria-label="Sluiten">',
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 17" fill="currentColor">',
          '<path d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268z"/>',
        '</svg>',
      '</button>',
      '<button class="nv-lb__btn nv-lb__prev" data-lb-prev aria-label="Vorige afbeelding">',
        '<svg viewBox="0 0 24 24"><polyline points="15 19 8 12 15 5"/></svg>',
      '</button>',
      '<div class="nv-lb__stage"><img class="nv-lb__img" src="" alt=""></div>',
      '<button class="nv-lb__btn nv-lb__next" data-lb-next aria-label="Volgende afbeelding">',
        '<svg viewBox="0 0 24 24"><polyline points="9 5 16 12 9 19"/></svg>',
      '</button>',
      '<div class="nv-lb__counter" aria-live="polite"></div>',
    ].join('');

    document.body.appendChild(lb);

    imgEl     = lb.querySelector('.nv-lb__img');
    prevBtn   = lb.querySelector('[data-lb-prev]');
    nextBtn   = lb.querySelector('[data-lb-next]');
    closeBtn  = lb.querySelector('[data-lb-close]');
    counterEl = lb.querySelector('.nv-lb__counter');

    closeBtn.addEventListener('click', closeLb);
    prevBtn.addEventListener('click', () => navigate(-1));
    nextBtn.addEventListener('click', () => navigate(1));

    /* Click on dark backdrop (not on image) → close */
    lb.addEventListener('click', (e) => {
      if (e.target === lb || e.target.classList.contains('nv-lb__stage')) closeLb();
    });

    document.addEventListener('keydown', onKeyDown);
  }

  /* ── Collect images from a gallery element ───────────────────────────────── */

  function collectImages(gallery) {
    return [...gallery.querySelectorAll(ITEM)].map((item) => {
      const el = item.querySelector('img');
      if (!el) return null;
      return {
        src: el.dataset.lbSrc || el.src,
        alt: el.alt || '',
      };
    }).filter(Boolean);
  }

  /* ── Open ─────────────────────────────────────────────────────────────────── */

  function openLb(gallery, idx) {
    build();

    images  = collectImages(gallery);
    current = Math.max(0, Math.min(idx, images.length - 1));
    lastFocus = document.activeElement;

    show(current);

    lb.setAttribute('aria-hidden', 'false');
    lb.classList.add('is-open');

    const sbW = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow     = 'hidden';
    document.body.style.paddingRight = sbW > 0 ? sbW + 'px' : '';

    requestAnimationFrame(() => closeBtn.focus());
  }

  /* ── Close ────────────────────────────────────────────────────────────────── */

  function closeLb() {
    lb.classList.remove('is-open');
    lb.setAttribute('aria-hidden', 'true');

    document.body.style.overflow     = '';
    document.body.style.paddingRight = '';

    lastFocus?.focus();
    lastFocus = null;
  }

  /* ── Navigate ─────────────────────────────────────────────────────────────── */

  function navigate(dir) {
    current = (current + dir + images.length) % images.length;
    show(current);
  }

  /* ── Show image at index ─────────────────────────────────────────────────── */

  function show(idx) {
    const entry = images[idx];
    if (!entry) return;

    imgEl.classList.remove('is-visible');
    imgEl.alt = entry.alt;
    imgEl.src = '';

    const fresh = new Image();
    fresh.onload = () => {
      imgEl.src = fresh.src;
      imgEl.classList.add('is-visible');
    };
    fresh.onerror = () => {
      imgEl.src = entry.src;
      imgEl.classList.add('is-visible');
    };
    fresh.src = entry.src;

    const single = images.length <= 1;
    prevBtn.hidden = single;
    nextBtn.hidden = single;
    counterEl.textContent = single ? '' : `${idx + 1} / ${images.length}`;
  }

  /* ── Keyboard ─────────────────────────────────────────────────────────────── */

  function onKeyDown(e) {
    if (!lb?.classList.contains('is-open')) return;

    switch (e.key) {
      case 'Escape':      closeLb();       break;
      case 'ArrowLeft':   navigate(-1);    break;
      case 'ArrowRight':  navigate(1);     break;
      case 'Tab': {
        const focusable = [closeBtn, prevBtn, nextBtn].filter((el) => el && !el.hidden);
        if (!focusable.length) break;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
        break;
      }
    }
  }

  /* ── Bind click handlers to a gallery ───────────────────────────────────── */

  function bindGallery(gallery) {
    const items = [...gallery.querySelectorAll(ITEM)];
    items.forEach((item, idx) => {
      item.addEventListener('click', () => openLb(gallery, idx));

      /* Real <button>s are already focusable and keyboard-operable */
      if (item.tagName === 'BUTTON') return;

      /* Keyboard accessibility — make item focusable */
      if (!item.hasAttribute('tabindex')) {
        item.setAttribute('tabindex', '0');
        item.setAttribute('role', 'button');
        item.setAttribute('aria-label', 'Afbeelding vergroten');
      }

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openLb(gallery, idx);
        }
      });
    });
  }

  /* ── Init ─────────────────────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll(GALLERY).forEach(bindGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
